import { apiFetch, type TokenStore } from './client.ts';

/**
 * Documents API client (spec §8; PERMISSION_MATRIX §14; invariant 9 - issued
 * documents are immutable). Case-scoped lifecycle: draft → pending_review →
 * issued (multi-actor: author ≠ reviewer; signature requirements per document
 * kind). Issued documents render read-only in the UI - the server 409s any
 * mutation attempt, and the UI offers no edit affordance.
 *
 * Document rows are returned snake_case (the raw DB shape the route sends); the
 * client never receives a storage_key - file access is only via GET /files/:id.
 */

export type DocumentStatus =
  | 'draft'
  | 'pending_review'
  | 'issued'
  | 'superseded'
  | 'withdrawn';

export interface DocumentRow {
  id: string;
  practice_id: string;
  case_id: string;
  kind_code: string;
  status: DocumentStatus;
  payload_json: string;
  authored_by: string;
  reviewed_by: string | null;
  reviewed_at: number | null;
  issued_by: string | null;
  issued_at: number | null;
  pdf_file_object_id: string | null;
  /** Only meaningful for kind_code 'common.uploaded' — other kinds' tutor
   *  visibility is a fixed rule, not a per-document choice. */
  tutor_visible: boolean;
  created_at: number;
}

/**
 * A row from the case documents LIST, which additionally carries the signature
 * gate that decides whether the document can be issued. Only the list endpoint
 * returns these; `getDocument` returns the bare row plus a `signatures` array,
 * so the two shapes are deliberately distinct types rather than one optional-
 * field union that would let a caller read `required_signer_roles` from a
 * response that never contains it.
 */
export interface DocumentListRow extends DocumentRow {
  /** Signer roles this document's kind requires before it can be issued. */
  required_signer_roles: string[];
  /** Signer roles already recorded against this document. */
  signed_roles: string[];
}

export interface DocumentSignature {
  id: string;
  signer_role: string;
  signature_method: string;
  signed_at: number;
}

export async function listDocuments(caseId: string, store: TokenStore): Promise<DocumentListRow[]> {
  const res = await apiFetch<{ documents: DocumentListRow[] }>(
    `/cases/${encodeURIComponent(caseId)}/documents`,
    {},
    store,
  );
  return res.documents;
}

export async function getDocument(
  documentId: string,
  store: TokenStore,
): Promise<{ document: DocumentRow; signatures: DocumentSignature[] }> {
  return apiFetch<{ document: DocumentRow; signatures: DocumentSignature[] }>(
    `/documents/${encodeURIComponent(documentId)}`,
    {},
    store,
  );
}

export async function createDraft(
  caseId: string,
  input: { kindCode: string; payloadJson?: string },
  store: TokenStore,
): Promise<string> {
  const res = await apiFetch<{ documentId: string }>(
    `/cases/${encodeURIComponent(caseId)}/documents`,
    {
      method: 'POST',
      body: JSON.stringify({ kind_code: input.kindCode, payload_json: input.payloadJson ?? '{}' }),
    },
    store,
  );
  return res.documentId;
}

/**
 * Generate a programme document draft (V6 gap 7-B): the server snapshots the
 * case's currently-active skill + behaviour targets into a new draft, which then
 * rides the normal review → issue → share-to-client lifecycle.
 */
export async function generateProgramme(caseId: string, store: TokenStore): Promise<string> {
  const res = await apiFetch<{ documentId: string }>(
    `/cases/${encodeURIComponent(caseId)}/documents/programme`,
    { method: 'POST', body: JSON.stringify({}) },
    store,
  );
  return res.documentId;
}

/** Read a browser File into a base64 string (no data: URI prefix). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload an externally-authored document (e.g. an assessment report) against a
 * case. The uploaded file becomes an issued, immutable
 * document that can then be shared with the client via the share-token path.
 */
export async function uploadDocument(
  caseId: string,
  input: { title: string; file: File },
  store: TokenStore,
): Promise<{ documentId: string; fileObjectId: string }> {
  const contentBase64 = await fileToBase64(input.file);
  return apiFetch<{ documentId: string; fileObjectId: string }>(
    `/cases/${encodeURIComponent(caseId)}/documents/upload`,
    {
      method: 'POST',
      body: JSON.stringify({
        title: input.title,
        filename: input.file.name,
        mimeType: input.file.type,
        contentBase64,
      }),
    },
    store,
  );
}

export async function submitForReview(documentId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/documents/${encodeURIComponent(documentId)}/submit-for-review`,
    { method: 'POST' },
    store,
  );
}

/** Discard a draft (beta feedback 2026-07-30) - author or owner only, draft status only. */
export async function discardDocument(
  documentId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/documents/${encodeURIComponent(documentId)}/discard`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

export async function reviewDocument(
  documentId: string,
  approved: boolean,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/documents/${encodeURIComponent(documentId)}/review`,
    { method: 'POST', body: JSON.stringify({ approved }) },
    store,
  );
}

export type SignerRole = 'author' | 'reviewer' | 'client_contact' | 'witness';
export type SignatureMethod = 'typed' | 'drawn' | 'electronic_ack';

export async function signDocument(
  documentId: string,
  input: { signerRole: SignerRole; signatureMethod: SignatureMethod },
  store: TokenStore,
): Promise<string> {
  const res = await apiFetch<{ signatureId: string }>(
    `/documents/${encodeURIComponent(documentId)}/sign`,
    {
      method: 'POST',
      body: JSON.stringify({ signer_role: input.signerRole, signature_method: input.signatureMethod }),
    },
    store,
  );
  return res.signatureId;
}

export async function issueDocument(documentId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/documents/${encodeURIComponent(documentId)}/issue`,
    { method: 'POST' },
    store,
  );
}

/** A tutor's lightweight "I've read and understood this" (FB-0055) — not a signature.
 *  POST /documents/:id/acknowledge */
export async function acknowledgeDocument(documentId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ id: string }>(
    `/documents/${encodeURIComponent(documentId)}/acknowledge`,
    { method: 'POST' },
    store,
  );
}

/** Withdraw an issued document. Case-lead-only (case.withdraw_documents); issued status only. */
export async function withdrawDocument(
  documentId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/documents/${encodeURIComponent(documentId)}/withdraw`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

/** Supersede an issued document with a new draft. Carries forward the same
 *  payload unless `payloadJson` is given. Issued status only. */
export async function supersedeDocument(
  documentId: string,
  input: { reason: string; payloadJson?: string },
  store: TokenStore,
): Promise<string> {
  const res = await apiFetch<{ newDocumentId: string }>(
    `/documents/${encodeURIComponent(documentId)}/supersede`,
    {
      method: 'POST',
      body: JSON.stringify({
        reason: input.reason,
        ...(input.payloadJson !== undefined && { payload_json: input.payloadJson }),
      }),
    },
    store,
  );
  return res.newDocumentId;
}

/** Grant or revoke a tutor's access to one uploaded document. Only meaningful
 *  for kind_code 'common.uploaded' — the server refuses (NOT_APPLICABLE) for
 *  every other kind. PATCH /documents/:id/tutor-visibility */
export async function setDocumentTutorVisibility(
  documentId: string,
  tutorVisible: boolean,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/documents/${encodeURIComponent(documentId)}/tutor-visibility`,
    { method: 'PATCH', body: JSON.stringify({ tutorVisible }) },
    store,
  );
}
